import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatInput } from 'features/chat/components/ChatInput';
import { ChatMessages } from 'features/chat/components/ChatMessages';
import { Globe, Images, LoaderCircle, Mic } from 'lucide-react';
import { ChatPageParams } from './hooks/useConversationById';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useChatStore } from '../../state/store/chat/use-chat-store';
import { useIsMobile } from '../../hooks/use-mobile';
import { Attachment } from '/features/chat/types/chat';
import { useModelStore } from '../../state/store/chat/use-model-store';
import { useToast } from '../../hooks/use-toast';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useConfig } from 'hooks/use-config';

export enum ButtonTypes {
  BUTTON,
  ICON,
}

export enum Alignment {
  LEFT,
  RIGHT,
}

export enum ButtonIds {
  SEARCH,
  CREATE_IMAGE,
  VOICE,
}

export interface ChatInputButton {
  id: ButtonIds;
  label?: string;
  icon: React.ReactNode;
  onClick: () => void;
  type: ButtonTypes;
  alignment: Alignment;
  isDisabled?: boolean;
  isSelected?: boolean;
}

const initialFeaturesState: Record<ButtonIds, boolean> = {
  [ButtonIds.SEARCH]: false,
  [ButtonIds.CREATE_IMAGE]: false,
  [ButtonIds.VOICE]: false,
};

export default function ChatPage() {
  const { t } = useTranslation();
  const accessToken = useAuthStore.getState().accessToken;
  const [inputButtons, setInputButtons] = useState<ChatInputButton[]>([]);
  const [isFeatureSelected, setIsFeatureSelected] =
    useState<Record<ButtonIds, boolean>>(initialFeaturesState);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { conversationId: conversationIdFromParams } = useParams<ChatPageParams>();
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const setActiveConversation = useChatStore((state) => state.setActiveConversation);
  const conversations = useChatStore((state) => state.conversations);
  const initializeWebSocket = useChatStore((state) => state.initializeWebSocket);
  const webSocketStatus = useChatStore((state) => state.webSocketStatus);
  const sendMessageToStore = useChatStore((state) => state.sendMessage);

  const currentConversation = activeConversationId ? conversations[activeConversationId] : null;
  const messages = currentConversation?.messages ?? [];
  const isLoading: boolean = currentConversation?.isLoading ?? webSocketStatus === 'connecting';
  const isCurrentMessageLoading: boolean = currentConversation?.isCurrentMessageLoading ?? false;
  const isCurrentResponseGenerating: boolean =
    currentConversation?.isCurrentResponseGenerating ?? false;
  const error = currentConversation?.error;
  const { toast } = useToast();
  const setSelectedModel = useModelStore.getState().setSelectedModel;
  const selectedModel = useModelStore.getState().selectedModel;
  const location = useLocation();
  const { configurationSettings } = useConfig();

  useEffect(() => {
    if (location.pathname === '/chat') {
      setIsFeatureSelected(initialFeaturesState);
    }
  }, [location.pathname]);

  useEffect(() => {
    const modelName = currentConversation?.modelName ?? selectedModel?.id;
    setSelectedModel(modelName);
  });

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error,
      });
    }
  }, [error]);

  useEffect(() => {
    if (accessToken) {
      initializeWebSocket(configurationSettings);
    }
  }, [accessToken, initializeWebSocket]);

  useEffect(() => {
    setActiveConversation(conversationIdFromParams ?? null, configurationSettings);
  }, [conversationIdFromParams, setActiveConversation]);

  const sendMessage = (content: string, files: Attachment[]) => {
    if (!activeConversationId) {
      const newConversationId = uuidv4();
      sendMessageToStore(
        content,
        configurationSettings,
        newConversationId,
        files,
        isFeatureSelected
      );
      navigate(`/chat/${newConversationId}`);
    } else {
      sendMessageToStore(content, configurationSettings, undefined, files, isFeatureSelected);
    }
  };

  useEffect(() => {
    setInputButtons([
      {
        id: ButtonIds.SEARCH,
        label: t('SEARCH'),
        icon: <Globe size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: false,
        isSelected: isFeatureSelected[ButtonIds.SEARCH],
      },
      {
        id: ButtonIds.CREATE_IMAGE,
        label: t('CREATE_IMAGE'),
        icon: <Images size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
          }));
        },
        type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
        alignment: Alignment.LEFT,
        isDisabled: true,
        isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
      },
      {
        id: ButtonIds.VOICE,
        icon: <Mic size={16} />,
        onClick: () => {
          setIsFeatureSelected((prev) => ({
            ...initialFeaturesState,
            [ButtonIds.VOICE]: !prev[ButtonIds.VOICE],
          }));
        },
        type: ButtonTypes.ICON,
        alignment: Alignment.RIGHT,
        isDisabled: true,
        isSelected: isFeatureSelected[ButtonIds.VOICE],
      },
    ]);
  }, [isMobile, messages.length, isFeatureSelected]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center w-full">
        <LoaderCircle className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col ${messages.length === 0 ? 'justify-center' : 'justify-start'} items-center w-full`}
    >
      {messages.length > 0 && (
        <ChatMessages messages={messages} isLoading={isCurrentMessageLoading} />
      )}
      {messages.length == 0 && (
        <div className="mb-8 text-center">
          <span className="text-[32px] font-bold">{t('GENERAL_GREETINGS')}</span>
        </div>
      )}
      <div
        className={`${messages.length === 0 ? 'flex' : 'fixed'} bottom-8 sm:bottom-6 md:bottom-8 w-[90%] md:w-[48%] lg:w-[51%] flex items-center justify-center shadow-[var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)]`}
      >
        <ChatInput
          onSendMessage={sendMessage}
          isLoading={isCurrentMessageLoading}
          inputButtons={inputButtons}
          isCurrentResponseGenerating={isCurrentResponseGenerating}
        />
      </div>
    </div>
  );
}
